import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuInfo } from 'rc-menu/es/interface';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import type { MenuProps } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { PageLayout, SideBarProps, TopBarProps, type AboutInfo, VERSION as ENSO_VERSION, useThemeMenuItem } from '@ohpen/enso-ui';
import { getCdnAssets, useConfig, type CdnAssets } from '@ohpen/react-utils';
import { useAuthz } from '@ohpen/react-authz';
import { useAuth } from '@ohpen/react-auth';

import { AppMenuItem, AppLayoutProps } from './interfaces';
import { getSideBarItems } from './sideBarItems';
import { UserProfileModal } from './UserProfileModal';
import styles from './styles.module.css';
import { FeatureFlag, Permission, Role, getSignInMethod } from '../../contexts/AuthzProvider';
import { getUserGroups } from '../../contexts/AuthzProvider/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { PmpConfigContext } from '@pmp/contexts';
import { fetchAgentVersion, type AgentBuildInfo } from '../../assistant/agentVersion';
import type { PmpAssistantAppConfig } from '../../assistant/agentTransport';

// Cognito groups that map to a known PMP role. Anything else (other apps' groups,
// stray entries) is ignored when building the profile view.
const KNOWN_ROLES = new Set<Role>([
  'PMP_ReadOnly',
  'PMP_Update',
  'PMP_Reviewers',
  'PMP_SelfReviewer',
  'PMP_Lenders',
  'PMP_LenderAdmin',
  'PMP_LenderViewer',
  'PMP_LenderSupport',
]);

export const AppLayout = ({ children }: AppLayoutProps): ReactElement => {
  const config = useConfig<PmpAssistantAppConfig>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const auth = useAuth();
  const { userPermissions, envFeatureFlags } = useAuthz();
  const enableDarkMode = useFeatureToggle(['enableDarkMode']);
  const assistantEnabled = useFeatureToggle(['enableAssistant']);
  const pmpConfig = useContext(PmpConfigContext);
  const { CLIENT: client, STAGE: stage, DEVELOPMENT_OVERRIDES } = config;


  const [selectedClient, setSelectedClient] = useState<string>(client);
  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);
  const [agentBuild, setAgentBuild] = useState<AgentBuildInfo | null>(null);

  useEffect(() => {
    if (stage === 'dev') {
      setSelectedClient(localStorage.getItem('dev-theme') ?? client);
    }
  }, [client, stage]);

  useEffect(() => {
    getCdnAssets({ ...config, CLIENT: selectedClient }).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [config, selectedClient]);

  // Only asked for where the assistant actually runs, so an environment without it neither shows
  // the row nor calls an agent that is not there.
  useEffect(() => {
    if (!assistantEnabled) {
      setAgentBuild(null);
      return;
    }
    const controller = new AbortController();
    fetchAgentVersion(config.APP?.agentUrl, controller.signal).then(setAgentBuild);
    return () => controller.abort();
  }, [assistantEnabled, config.APP?.agentUrl]);

  const onMenuItemClick = useCallback(
    (info: MenuInfo) => {
      navigate(`/${info.key}`);
    },
    [navigate],
  );

  const sideBarItems: AppMenuItem[] = useMemo(
    () => getSideBarItems(userPermissions as Set<Permission>, envFeatureFlags as Set<FeatureFlag>),
    [userPermissions, envFeatureFlags],
  );

  const selected = useMemo(() => {
    const key = location.pathname.split('/')[1];
    const item = sideBarItems?.find(item => item?.key === key);
    return item?.key;
  }, [location.pathname, sideBarItems]);

  const items: AppMenuItem[] = useMemo(
    () =>
      sideBarItems.map(item => (item && 'label' in item && typeof item.label === 'string' ? { ...item, label: t(item.label) } : item)) ??
      [],
    [sideBarItems, t],
  );

  const sideBar: SideBarProps = useMemo(
    () => ({
      items: items as any,
      selectedKeys: [String(selected)],
      onClick: onMenuItemClick,
      width: 220,
    }),
    [items, onMenuItemClick, selected],
  );

  const { userDisplayName: displayName } = auth;

  const [profileOpen, setProfileOpen] = useState(false);

  const userRoles = useMemo(() => {
    const groups = getUserGroups(auth.user?.attributes?.['custom:groups']);
    return groups.filter((r): r is Role => KNOWN_ROLES.has(r as Role));
  }, [auth.user?.attributes]);

  const signInMethod = useMemo(() => getSignInMethod(auth.user?.attributes), [auth.user?.attributes]);

  const userEmail = auth.user?.attributes?.email;

  // Placed here rather than via enso's showDarkModeToggle, which always renders after
  // extraItems: "My account" has to stay below the appearance entry.
  const themeMenuItem = useThemeMenuItem(enableDarkMode);

  const userMenuExtraItems: MenuProps['items'] = useMemo(() => {
    const entries: MenuProps['items'] = [];
    if (enableDarkMode) {
      entries.push(themeMenuItem);
    }
    entries.push({
      key: 'my-account',
      icon: <UserOutlined />,
      label: t('global.labels.myAccount'),
      onClick: () => setProfileOpen(true),
    });
    return entries;
  }, [enableDarkMode, themeMenuItem, t]);

  const onClickLogout = useCallback(async () => {
    await auth.logout();
  }, [auth]);

  const onChangeClient = useCallback((value: string) => {
    setSelectedClient(value);
    localStorage.setItem('dev-theme', value);
  }, []);

  const customMenu = useMemo(() => {
    const clientsList = DEVELOPMENT_OVERRIDES?.CLIENTS_LIST;
    if (!clientsList || stage !== 'dev') return undefined;
    const options = clientsList.map((client: string) => ({ value: client, label: client }));

    return (
      <Select className={styles.clientSelect} value={selectedClient} onChange={onChangeClient} options={options} variant="borderless" />
    );
  }, [DEVELOPMENT_OVERRIDES?.CLIENTS_LIST, onChangeClient, selectedClient, stage]);

  const aboutInfo: AboutInfo = useMemo(
    () => ({
      // Versions first, in the order a reader cares about (the portal, then what it talks to), then
      // where they are running.
      items: [
        { label: t('global.labels.pmpVersion'), value: config.VERSION || '-' },
        { label: t('global.labels.brsVersion'), value: pmpConfig?.brsVersion || '-' },
        ...(agentBuild ? [{ label: t('global.labels.agentVersion'), value: agentBuild.version }] : []),
        { label: t('global.labels.ensoUiVersion'), value: ENSO_VERSION },
        { label: t('global.labels.stage'), value: config.STAGE || '-' },
        { label: t('global.labels.client'), value: config.CLIENT || '-' },
      ],
    }),
    [agentBuild, config, pmpConfig, t],
  );

  const topBar: TopBarProps = useMemo(
    () => ({
      logo: { src: cdnAssets?.logoHorizontalPath ?? '', href: '/' },
      appName: t('global.title'),
      userMenu: {
        name: displayName,
        onClickLogout,
        aboutInfo,
        extraItems: userMenuExtraItems,
      },
      customMenu,
    }),
    [cdnAssets?.logoHorizontalPath, customMenu, onClickLogout, displayName, aboutInfo, userMenuExtraItems, t],
  );

  return (
    <PageLayout sideBar={sideBar} topBar={topBar}>
      <UserProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        name={displayName}
        email={userEmail}
        signInMethod={signInMethod}
        roles={userRoles}
      />
      {children}
    </PageLayout>
  );
};
